<script setup lang="ts">
  import { ref, computed, onMounted } from 'vue';
  import { VueFinalModal } from 'vue-final-modal';
  import {
    UiModalHeader,
    FormInput,
    ColoredButton,
    ThemeProvider,
    CustomCheckbox,
    SimpleLoading,
  } from '@/shared/ui';
  import { positionApi, type Position } from '@/entities/Position';
  import { MAX_DEPARTMENT_CODE_LENGTH } from '@/entities/Department';
  import CheckMarkIcon from '@/shared/assets/icons/CheckMarkIcon.svg';
  import { useCustomToast, fetchAllPages } from '@/shared/lib';

  defineProps<{
    title?: string;
  }>();

  const emit = defineEmits<{
    (e: 'close'): void;
    (e: 'save', data: { code: string; positionIds: number[] }): void;
  }>();

  const { showErrorToast } = useCustomToast();

  const departmentCode = ref('');
  const positions = ref<Position[]>([]);
  const selectedPositionIds = ref<Set<number>>(new Set());
  const isLoadingPositions = ref(false);

  const isFormValid = computed(() => {
    return !!departmentCode.value.trim();
  });

  const selectedCount = computed(() => selectedPositionIds.value.size);

  const allPositionsSelected = computed(() => {
    return positions.value.length > 0 && positions.value.length === selectedCount.value;
  });

  onMounted(async () => {
    await loadPositions();
  });

  const loadPositions = async () => {
    isLoadingPositions.value = true;
    try {
      positions.value = await fetchAllPages((page) =>
        positionApi.getAllPositions({ page, itemsPerPage: 1000 })
      );
    } catch {
      showErrorToast({
        title: 'Erro ao carregar cargos',
        type: 'error',
      });
    } finally {
      isLoadingPositions.value = false;
    }
  };

  const togglePositionSelection = (positionId: number) => {
    if (selectedPositionIds.value.has(positionId)) {
      selectedPositionIds.value.delete(positionId);
    } else {
      selectedPositionIds.value.add(positionId);
    }
  };

  const toggleSelectAll = () => {
    if (allPositionsSelected.value) {
      selectedPositionIds.value.clear();
    } else {
      selectedPositionIds.value.clear();
      positions.value.forEach((position) => {
        selectedPositionIds.value.add(position.id);
      });
    }
  };

  const isPositionSelected = (positionId: number) => {
    return selectedPositionIds.value.has(positionId);
  };

  const handleClose = () => {
    emit('close');
  };

  const handleSave = () => {
    emit('save', {
      code: departmentCode.value.trim(),
      positionIds: Array.from(selectedPositionIds.value),
    });
  };

  const handleClosed = () => {
    departmentCode.value = '';
    selectedPositionIds.value.clear();
  };

  const handleDepartmentCodeInput = (value: string) => {
    if (value.length <= MAX_DEPARTMENT_CODE_LENGTH) {
      departmentCode.value = value;
    }
  };
</script>

<template>
  <VueFinalModal
    class="flex justify-center items-center"
    content-class="flex flex-col bg-white-01 rounded-2xl w-[1000px] shadow-lg"
    overlay-transition="vfm-fade"
    content-transition="vfm-fade"
    @closed="handleClosed"
  >
    <ThemeProvider class="flex flex-col">
      <div class="flex flex-col">
        <UiModalHeader
          class="px-6 py-4 text-sm border-b border-gray-02 shrink-0"
          :title="title || 'Novo Departamento'"
          @close="handleClose"
        />

        <div class="px-6 py-6 space-y-6">
          <FormInput
            id="department-code"
            :model-value="departmentCode"
            label="Departamento:"
            type="text"
            placeholder="Escreva o título do Departamento"
            label-class="text-sm text-gray-07 font-semibold"
            :max-length="MAX_DEPARTMENT_CODE_LENGTH"
            @update:model-value="handleDepartmentCodeInput"
          />

          <hr class="border-t border-gray-02 -mx-6">

          <div class="flex flex-col gap-2">
            <label class="text-sm text-gray-07 font-semibold">Cargos:</label>
            <span class="text-xs text-gray-05">
              Selecione os cargos que pertencem ao Departamento
            </span>

            <div
              v-if="isLoadingPositions"
              class="flex justify-center py-8"
            >
              <SimpleLoading />
            </div>

            <div
              v-else
              class="border rounded-lg h-96 overflow-hidden flex flex-col"
            >
              <!-- Header -->
              <div class="px-4 py-2 sticky top-0 z-10">
                <span class="text-sm text-gray-07 font-semibold">Cargos</span>
              </div>

              <!-- List -->
              <div class="overflow-y-auto flex-1">
                <div
                  v-for="position in positions"
                  :key="position.id"
                  class="flex items-center py-2 hover:bg-gray-50 px-4 cursor-pointer"
                  @click="togglePositionSelection(position.id)"
                >
                  <CustomCheckbox
                    :id="`position-${position.id}`"
                    :checked="isPositionSelected(position.id)"
                  />
                  <span class="ml-3 text-sm text-gray-07 select-none">
                    {{ position.name }}
                  </span>
                </div>

                <div
                  v-if="positions.length === 0"
                  class="flex justify-center items-center h-full text-gray-05"
                >
                  Nenhum cargo disponível
                </div>
              </div>
            </div>

            <span class="text-xs text-gray-05">
              Total de cargos selecionados: {{ selectedCount }}
            </span>
          </div>
        </div>

        <div class="px-6 py-8 flex justify-end gap-3 border-t border-gray-02 shrink-0">
          <ColoredButton
            text="Cancelar"
            outlined
            color="gray-03"
            @click="handleClose"
          />
          <ColoredButton
            :disabled="!isFormValid"
            text="Salvar"
            :icon="CheckMarkIcon"
            @click="handleSave"
          />
        </div>
      </div>
    </ThemeProvider>
  </VueFinalModal>
</template>

<style scoped>
  .vfm__overlay {
    background-color: rgba(0, 0, 0, 0.5);
  }
</style>
